import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  LayoutDashboard, 
  Package, 
  FolderTree, 
  ShoppingCart, 
  ArrowLeft,
  Settings,
  Mail,
  Users,
  MessageSquare,
  BookOpen,
  Newspaper
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState({ products: 0, categories: 0, orders: 0, messages: 0 });

  const isMainDashboard = location.pathname === '/admin';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, categoriesRes, ordersRes, messagesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/admin/orders`),
          axios.get(`${API}/admin/messages`),
        ]);
        setStats({
          products: productsRes.data.length,
          categories: categoriesRes.data.length,
          orders: ordersRes.data.length,
          messages: messagesRes.data.length,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    if (isAdmin) fetchStats();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="font-serif text-2xl font-bold text-foreground mb-4">Access Denied</h1>
        <p className="text-muted-foreground mb-6">You don&apos;t have permission to access this area.</p>
        <Link to="/">
          <Button className="btn-primary">Go Home</Button>
        </Link>
      </div>
    );
  }

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/admin/products', icon: Package, label: 'Products' },
    { to: '/admin/categories', icon: FolderTree, label: 'Categories' },
    { to: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
    { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/admin/ebooks', icon: BookOpen, label: 'E-books' },
    { to: '/admin/blog', icon: Newspaper, label: 'Blog' },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Settings className="h-8 w-8 text-primary" />
            <div>
              <h1 className="font-serif text-2xl font-bold text-foreground">Admin Panel</h1>
              <p className="text-sm text-muted-foreground">Welcome, {user?.name}</p>
            </div>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Store
            </Button>
          </Link>
        </div>

        {/* Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          {navItems.map((item) => {
            const isActive = item.exact 
              ? location.pathname === item.to 
              : location.pathname.startsWith(item.to) && item.to !== '/admin';
            return (
              <Link key={item.to} to={item.to}>
                <Button 
                  variant={isActive ? 'default' : 'outline'} 
                  className={isActive ? 'btn-primary' : ''}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>

        {/* Main Content */}
        {isMainDashboard ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Products</CardTitle>
                <Package className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.products}</div>
                <Link to="/admin/products" className="text-sm text-primary hover:underline">
                  Manage products →
                </Link>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Categories</CardTitle>
                <FolderTree className="h-5 w-5 text-secondary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.categories}</div>
                <Link to="/admin/categories" className="text-sm text-primary hover:underline">
                  Manage categories →
                </Link>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Orders</CardTitle>
                <ShoppingCart className="h-5 w-5 text-accent" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.orders}</div>
                <Link to="/admin/orders" className="text-sm text-primary hover:underline">
                  View orders →
                </Link>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Messages</CardTitle>
                <Mail className="h-5 w-5 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.messages}</div>
                <Link to="/admin/messages" className="text-sm text-primary hover:underline">
                  View messages →
                </Link>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
};

export default Admin;
